import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Modal,
  TextField,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import POSection from './components/po_section.component';
import InvoiceDetailsSection from './components/invoice_details_section.component';
import RingDetailsSection from './components/ring_details_section.component';
import AdditionalInfoSection from './components/additional_info_section.component';
import FileUploadSection from './components/file_upload_section.component';
import MultipleFileUploadSection from './components/multiple_file_upload_section.component';
import { parsePDFFile } from './functions/parsePDF.function';
import { notify } from '../../common/functions/react-toastify.function';
import { createVendorInvoice } from './functions/create_vendor_invoice.function';
import { verifyPO } from './functions/verify_po.function';
import { getPoRingsDetail } from './functions/get_po_rings_detail.function';
import { getVendorInvoices } from './functions/get_vendor_invoices.function';
import { formatDisplayAmount } from './functions/format_display_amount.function';

const VendorInvoiceForm = () => {
  const navigate = useNavigate();
  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch (error) {
      console.error('Failed to parse permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!permissionNames.includes('Vendor Invoice Management')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  const [isParsingPDF, setIsParsingPDF] = useState(false);
  const [isVerifyingPO, setIsVerifyingPO] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingInvoiceNumber, setIsCheckingInvoiceNumber] = useState(false);
  const [invoiceNumberExists, setInvoiceNumberExists] = useState(false);
  const [poVerificationData, setPoVerificationData] = useState(null);
  const [ringDetailsData, setRingDetailsData] = useState([]);
  const [poUnitPrice, setPoUnitPrice] = useState('');
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [formData, setFormData] = useState({
    // PO Section
    vendor: '',
    poNumber: '',
    poAmount: '',
    poDate: '',
    deliverDate: '',
    poFile: null,

    // Invoice Details
    invoiceNumber: '',
    invoiceAmount: '',

    // Type of Invoice
    invoiceType: 'FTTS',
    type: '',

    // Ring Details (category & paymentMilestone per ring)
    rings: [
      {
        ringName: '',
        sectionName: '',
        category: '',
        paymentRingMilestone: '',
        unitRate: '',
        length: '',
        amount: '',
      },
    ],

    // Additional Information
    region: '',
    jazzRORegionalPOC: '',
    submittersEmail: '',
    siteIds: '',
    atpStatus: '',
    pacStatus: '',
    facStatus: '',
    nocRowIssuanceDate: '',
    completionPacDate: '',
    remainingPOAmount: '',

    // Vendor Remarks
    vendorRemarks: '',

    // Other Documents
    otherDocuments: [],
  });

  const handleBack = () => {
    navigate('/vendorInvoiceManagement');
  };

  const handleInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRingChange = (index, field, value) => {
    setFormData((prev) => {
      const newRings = [...prev.rings];
      newRings[index] = { ...newRings[index], [field]: value };

      // Auto-calculate amount when unitRate, length, or payment milestone changes
      // Amount = (length * milestone% / 100) * unitRate (length field shows total; amount uses dropdown %)
      if (field === 'unitRate' || field === 'length' || field === 'paymentRingMilestone') {
        const unitRate = parseFloat(String(newRings[index].unitRate || '').replace(/,/g, ''));
        const length = parseFloat(String(newRings[index].length || '').replace(/,/g, ''));
        const milestoneVal =
          newRings[index].paymentRingMilestone || newRings[index].paymentMilestone || '';
        const pct = (() => {
          const cleaned = String(milestoneVal).replace(/%/g, '').trim();
          const n = parseFloat(cleaned);
          return Number.isFinite(n) ? n : 0;
        })();

        if (
          !isNaN(unitRate) &&
          !isNaN(length) &&
          unitRate > 0 &&
          length > 0 &&
          pct > 0 &&
          pct <= 100
        ) {
          const calculatedAmount = length * (pct / 100) * unitRate;
          newRings[index].amount = formatDisplayAmount(calculatedAmount);
        }
      }

      return { ...prev, rings: newRings };
    });
  };

  const handleAddRing = () => {
    setFormData((prev) => ({
      ...prev,
      rings: [
        ...prev.rings,
        {
          ringName: '',
          sectionName: '',
          category: '',
          paymentRingMilestone: '',
          unitRate: poUnitPrice || '',
          length: '',
          amount: '',
        },
      ],
    }));
  };

  const handleRemoveRing = (index) => {
    setFormData((prev) => ({
      ...prev,
      rings: prev.rings.filter((_, i) => i !== index),
    }));
  };

  const handleFileChange = async (field, file) => {
    if (field === 'poFile' && file) {
      // Check if it's a PDF file
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        setIsParsingPDF(true);
        try {
          const extractedData = await parsePDFFile(file);
          // Check if any data was actually extracted
          const hasExtractedData =
            extractedData.vendor ||
            extractedData.poNumber ||
            extractedData.poDate ||
            extractedData.deliverDate ||
            extractedData.poAmount;
          console.log('extractedData', extractedData);

          if (hasExtractedData && extractedData.poNumber) {
            // Check if PO amount is null or zero before making API call
            const poAmountValue = extractedData.poAmount;
            const poAmountNum = poAmountValue
              ? parseFloat(String(poAmountValue).replace(/,/g, ''))
              : null;

            // if (
            //   poAmountNum === null ||
            //   poAmountNum === undefined ||
            //   poAmountNum === 0 ||
            //   isNaN(poAmountNum)
            // ) {
            //   // PO amount is null or zero - don't make API call and show error
            //   setFormData((prev) => ({
            //     ...prev,
            //     vendor: '',
            //     poNumber: '',
            //     poDate: '',
            //     deliverDate: '',
            //     poAmount: '',
            //     poFile: null,
            //   }));
            //   notify('error', 'PO amount should not be zero');
            //   return;
            // }

            // Verify PO number with backend
            setIsVerifyingPO(true);
            try {
              const poVerificationResult = await verifyPO(extractedData.poNumber);

              if (poVerificationResult.error && poVerificationResult.status === 404) {
                // PO not found - show error modal and navigate back
                setErrorModalMessage(
                  poVerificationResult.message ||
                    'No record found matching this PO number. Please verify the PO number and try again.'
                );
                setErrorModalOpen(true);
                // Reset form data
                setFormData((prev) => ({
                  ...prev,
                  vendor: '',
                  poNumber: '',
                  poDate: '',
                  deliverDate: '',
                  poAmount: '',
                  poFile: null,
                }));
                setPoVerificationData(null);
                setRingDetailsData([]);
                setPoUnitPrice('');
                return;
              } else if (poVerificationResult.error) {
                // Other error
                notify('error', poVerificationResult.message || 'Failed to verify PO number');
                setFormData((prev) => ({
                  ...prev,
                  vendor: '',
                  poNumber: '',
                  poDate: '',
                  deliverDate: '',
                  poAmount: '',
                  poFile: null,
                }));
                setPoVerificationData(null);
                setRingDetailsData([]);
                setPoUnitPrice('');
                return;
              } else {
                // PO verified successfully - store verification data
                setPoVerificationData(poVerificationResult);
                // Extract the actual response data
                const poData = poVerificationResult.response || poVerificationResult;
                // Use PDF unit price if parsed, otherwise fall back to API unit_rate_meter for ring unit rate
                const unitPriceFromPO =
                  extractedData.unitPrice ||
                  (poData.unit_rate_meter != null ? String(poData.unit_rate_meter) : '') ||
                  '';
                setPoUnitPrice(unitPriceFromPO);
                // Update form data with extracted values; fill ring unit rate from PO Unit Price or API unit_rate_meter
                setFormData((prev) => ({
                  ...prev,
                  [field]: file,
                  // Source-of-truth priority:
                  // 1) verifyPO API response (authoritative)
                  // 2) parsed PDF values (fallback)
                  vendor: poData.vendor || extractedData.vendor || '',
                  poNumber: poData.poNumber || extractedData.poNumber || '',
                  poDate: poData.poDate || extractedData.poDate || '',
                  deliverDate: extractedData.deliverDate || '',
                  poAmount:
                    (poData.totalPOAmount != null ? String(poData.totalPOAmount) : '') ||
                    extractedData.poAmount ||
                    '',
                  rings: prev.rings.map((r) => ({
                    ...r,
                    unitRate: unitPriceFromPO || r.unitRate || '',
                  })),
                }));
                // Fetch ring details by PO id; alert if none
                if (poData.id != null) {
                  const ringResult = await getPoRingsDetail(poData.id);
                  if (ringResult.success && Array.isArray(ringResult.data)) {
                    setRingDetailsData(ringResult.data);
                    if (ringResult.data.length === 0) {
                      notify('warning', 'No Ring data against this PO');
                    }
                  } else {
                    setRingDetailsData([]);
                    notify('warning', 'No Ring data against this PO');
                  }
                } else {
                  setRingDetailsData([]);
                }
              }
            } catch (error) {
              console.error('Error verifying PO:', error);
              notify('error', 'Failed to verify PO number. Please try again.');
              setFormData((prev) => ({
                ...prev,
                vendor: '',
                poNumber: '',
                poDate: '',
                deliverDate: '',
                poAmount: '',
                poFile: null,
              }));
              setPoVerificationData(null);
              setRingDetailsData([]);
              setPoUnitPrice('');
            } finally {
              setIsVerifyingPO(false);
            }
          } else {
            // No data extracted - don't set the file and show error
            setFormData((prev) => ({
              ...prev,
              vendor: '',
              poNumber: '',
              poDate: '',
              deliverDate: '',
              poAmount: '',
            }));
            setPoUnitPrice('');
            notify('error', 'Unable to extract data from PDF. Please check the file format');
          }
        } catch (error) {
          console.error('Error parsing PDF:', error);
          // Parsing failed - don't set the file and show error
          setFormData((prev) => ({
            ...prev,
            vendor: '',
            poNumber: '',
            poDate: '',
            deliverDate: '',
            poAmount: '',
          }));
          setPoUnitPrice('');
          notify('error', 'Failed to parse PDF. Please check the file format');
        } finally {
          setIsParsingPDF(false);
          setIsVerifyingPO(false);
        }
      } else {
        notify('error', 'Please upload a PDF file only.');
      }
    } else if (field === 'otherDocuments') {
      // For otherDocuments, handle array of files
      if (Array.isArray(file)) {
        setFormData((prev) => ({
          ...prev,
          [field]: file,
        }));
      } else {
        // If single file, add to array
        setFormData((prev) => ({
          ...prev,
          [field]: [...(prev.otherDocuments || []), file],
        }));
      }
    } else {
      // For other files, just save the file
      setFormData((prev) => ({
        ...prev,
        [field]: file,
      }));
    }
  };

  const handleCancel = () => {
    // Reset form or navigate away
    navigate('/vendorInvoiceManagement');
  };

  // Validation: Check if PO number is missing
  const isPONumberMissing = useMemo(() => {
    return !formData.poNumber || formData.poNumber.trim() === '';
  }, [formData.poNumber]);

  // Validation: invoice number required and must match format (7 segments separated by " - ")
  // Also disallow any special chars other than space/hyphen.
  const isInvoiceNumberMissing = useMemo(() => {
    const raw = formData.invoiceNumber?.trim() || '';
    if (!raw) {
      return true;
    }
    if (/[^a-zA-Z0-9 -]/.test(raw)) {
      return true;
    }
    const parts = raw.split(' - ').map((s) => s.trim());
    return parts.length !== 7 || parts.some((p) => !p);
  }, [formData.invoiceNumber]);

  const isTypeMissing = useMemo(() => {
    return !formData.type || formData.type.trim() === '';
  }, [formData.type]);

  const isRingsIncomplete = useMemo(() => {
    return formData.rings.some((ring) => {
      const ringName =
        formData.invoiceType === 'Non-FTTS'
          ? (ring.sectionName || ring.ringName || '').trim()
          : (ring.ringName || ring.sectionName || '').trim();
      const category = (ring.category || '').trim();
      const milestone = (ring.paymentRingMilestone || ring.paymentMilestone || '').trim();
      return !ringName || !category || !milestone;
    });
  }, [formData.rings, formData.invoiceType]);

  const isSaveDisabled =
    isPONumberMissing ||
    isSubmitting ||
    isCheckingInvoiceNumber ||
    invoiceNumberExists ||
    !formData.region?.trim() ||
    isInvoiceNumberMissing ||
    isTypeMissing ||
    isRingsIncomplete;

  // Check invoice number uniqueness (debounced)
  useEffect(() => {
    let alive = true;
    const raw = (formData.invoiceNumber || '').trim();
    if (!raw || isInvoiceNumberMissing) {
      setInvoiceNumberExists(false);
      setIsCheckingInvoiceNumber(false);
      return () => {};
    }

    setIsCheckingInvoiceNumber(true);
    const t = setTimeout(async () => {
      try {
        const res = await getVendorInvoices();
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        const exists = list.some((inv) => {
          const invNo = (inv.invoice_number || inv.invoiceNumber || '').trim();
          return invNo && invNo === raw;
        });
        if (alive) {
          setInvoiceNumberExists(exists);
        }
      } catch (e) {
        // If check fails, don't block user; allow backend to be source of truth.
        if (alive) {
          setInvoiceNumberExists(false);
        }
      } finally {
        if (alive) {
          setIsCheckingInvoiceNumber(false);
        }
      }
    }, 500);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [formData.invoiceNumber, isInvoiceNumberMissing]);

  // Set remaining PO amount when PO verification data is loaded (fixed, doesn't change when invoice amount changes)
  useEffect(() => {
    if (poVerificationData) {
      // Extract the actual response data (handle both wrapped and unwrapped responses)
      const poData = poVerificationData.response || poVerificationData;
      const totalPOAmount = parseFloat(poData.totalPOAmount) || 0;
      const totalCoveredAmount = parseFloat(poData.totalCoveredAmount) || 0;

      // Calculate remaining PO amount: totalPOAmount - totalCoveredAmount (fixed, doesn't include current invoice)
      const remainingAmount = totalPOAmount - totalCoveredAmount;

      // Set the remaining PO amount field (only updates when PO verification data changes, not when invoice amount changes)
      setFormData((prev) => ({
        ...prev,
        remainingPOAmount:
          remainingAmount > 0 ? formatDisplayAmount(remainingAmount) : formatDisplayAmount(0),
      }));
    } else {
      // Clear remaining PO amount if no PO verification data
      setFormData((prev) => ({
        ...prev,
        remainingPOAmount: '',
      }));
    }
  }, [poVerificationData]); // Only depends on poVerificationData, not invoiceAmount

  // Invoice amount = sum of ring amounts (read-only, derived from Ring Details)
  const ringsAmountSum = useMemo(() => {
    const sum = formData.rings.reduce((acc, ring) => {
      const cleaned = ring.amount ? String(ring.amount).replace(/,/g, '').trim() : '';
      const num = cleaned && !Number.isNaN(parseFloat(cleaned)) ? parseFloat(cleaned) : 0;
      return acc + num;
    }, 0);
    return sum;
  }, [formData.rings]);

  const ringsAmountSumFormatted = useMemo(() => {
    if (ringsAmountSum === 0) {
      return '';
    }
    return formatDisplayAmount(ringsAmountSum);
  }, [ringsAmountSum]);

  // Validation: only check for invalid numeric input in ring amount fields
  const amountMismatchError = useMemo(() => {
    const hasInvalidAmounts = formData.rings.some((ring) => {
      if (!ring.amount || ring.amount.trim() === '') {
        return false;
      }
      const cleaned = ring.amount.replace(/,/g, '').trim();
      const valid = /^-?\d*\.?\d+$/.test(cleaned) && !/[a-zA-Z]/.test(ring.amount);
      return !valid;
    });
    if (hasInvalidAmounts) {
      return {
        hasError: true,
        invalidInput: true,
        message: 'Please enter valid numeric amounts only',
      };
    }
    return null;
  }, [formData.rings]);

  const handleSave = async () => {
    // Validate required fields before saving
    if (isPONumberMissing) {
      notify('error', 'PO number is missing. Please upload a PDF file.');
      return;
    }

    // Validate PO file is uploaded
    if (!formData.poFile) {
      notify('error', 'Please upload a PO file.');
      return;
    }

    if (isInvoiceNumberMissing) {
      notify('error', 'Please enter an invoice number.');
      return;
    }

    if (invoiceNumberExists) {
      notify('error', 'Invoice Number already exists. Please change the Sequence.');
      return;
    }

    if (isTypeMissing) {
      notify('error', 'Please select a type in Type of Invoice.');
      return;
    }

    if (isRingsIncomplete) {
      const ringLabel = formData.invoiceType === 'Non-FTTS' ? 'section name' : 'ring name';
      notify('error', `Please select ${ringLabel}, category, and payment milestone for each ring.`);
      return;
    }

    // Validate region is selected (required in Additional Information)
    if (!formData.region?.trim()) {
      notify('error', 'Please select a region in Additional Information.');
      return;
    }

    // Validate invoice amount does not exceed remaining PO amount
    if (!poVerificationData) {
      notify('error', 'Please verify PO before submitting.');
      return;
    }
    const poData = poVerificationData.response || poVerificationData;
    const totalPOAmount = parseFloat(poData.totalPOAmount) || 0;
    const totalCoveredAmount = parseFloat(poData.totalCoveredAmount) || 0;
    const remainingAmount = Math.max(0, totalPOAmount - totalCoveredAmount);
    const invoiceAmountNum = Number(String(ringsAmountSum || 0));

    if (!invoiceAmountNum || invoiceAmountNum <= 0) {
      notify('error', 'Invoice Amount must be greater than 0.');
      return;
    }

    if (invoiceAmountNum > remainingAmount) {
      notify(
        'error',
        `Invoice Amount cannot be more than remaining PO amount. Remaining: ${remainingAmount.toLocaleString()}`
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Transform rings data for payload based on invoice type
      const transformedRings = formData.rings.map((ring) => {
        const { ringName, sectionName, ...rest } = ring;

        // If Non-FTTS, use sectionName, otherwise use ringName
        if (formData.invoiceType === 'Non-FTTS') {
          return {
            ...rest,
            sectionName: sectionName || ringName || '', // Fallback to ringName if sectionName is empty
          };
        } else {
          return {
            ...rest,
            ringName: ringName || sectionName || '', // Fallback to sectionName if ringName is empty
          };
        }
      });

      // Create FormData for multipart/form-data submission
      const submitFormData = new FormData();

      // Append all form fields (excluding files)
      submitFormData.append('vendor', formData.vendor || '');
      submitFormData.append('poNumber', formData.poNumber || '');
      submitFormData.append('poAmount', formData.poAmount || '');
      submitFormData.append('poDate', formData.poDate || '');
      submitFormData.append('deliverDate', formData.deliverDate || '');
      submitFormData.append('invoiceNumber', formData.invoiceNumber || '');
      submitFormData.append(
        'invoiceAmount',
        ringsAmountSumFormatted || String(ringsAmountSum) || ''
      );
      submitFormData.append('category', formData.rings?.[0]?.category || '');
      submitFormData.append('paymentMilestone', formData.rings?.[0]?.paymentRingMilestone || '');
      submitFormData.append('invoiceType', formData.invoiceType || 'FTTS');
      submitFormData.append('type', formData.type || '');
      submitFormData.append('region', formData.region || '');
      submitFormData.append('jazzRORegionalPOC', formData.jazzRORegionalPOC || '');
      submitFormData.append('submittersEmail', formData.submittersEmail || '');
      submitFormData.append('siteIds', formData.siteIds || '');
      submitFormData.append('atpStatus', formData.atpStatus || '');
      submitFormData.append('pacStatus', formData.pacStatus || '');
      submitFormData.append('facStatus', formData.facStatus || '');
      submitFormData.append('nocRowIssuanceDate', formData.nocRowIssuanceDate || '');
      submitFormData.append('completionPacDate', formData.completionPacDate || '');
      submitFormData.append('remainingPOAmount', formData.remainingPOAmount || '');
      submitFormData.append('vendorRemarks', formData.vendorRemarks || '');

      // Append rings as JSON string
      submitFormData.append('rings', JSON.stringify(transformedRings));

      // Append PO file
      submitFormData.append('poFile', formData.poFile);

      // Append other documents (multiple files)
      if (
        formData.otherDocuments &&
        Array.isArray(formData.otherDocuments) &&
        formData.otherDocuments.length > 0
      ) {
        formData.otherDocuments.forEach((file, index) => {
          submitFormData.append('otherDocuments', file);
        });
      }

      // Make API call
      const response = await createVendorInvoice(submitFormData);

      // Handle success
      if (response) {
        notify('success', 'Vendor invoice created successfully');
        // Navigate back to vendor invoice management page
        navigate('/vendorInvoiceManagement');
      }
    } catch (error) {
      // Error is already handled by handleApiError in the function
      console.error('Error creating vendor invoice:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout padding={true}>
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Box
          sx={{
            margin: '0 auto',
            background: 'white',
            borderRadius: '8px',
            padding: '24px',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton
              onClick={handleBack}
              disableRipple
              sx={{
                mr: 1,
                backgroundColor: '#FFF',
                width: 40,
                height: 40,
                borderRadius: '8px',
                boxShadow: '0 0 30px 2px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <ArrowBackIosIcon sx={{ ml: '2px' }} />
            </IconButton>

            <Typography
              sx={{
                fontSize: '24px',
                fontFamily: 'Inter',
                fontWeight: 700,
                color: 'black',
              }}
            >
              Vendor Invoice
            </Typography>
          </Box>

          {/* PO File Upload Section */}
          <Box sx={{ position: 'relative' }}>
            {(isParsingPDF || isVerifyingPO) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(2px)',
                  borderRadius: '4px',
                }}
              >
                <CircularProgress size={40} sx={{ color: '#BD1D23' }} />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    color: '#666',
                  }}
                >
                  Processing...
                </Typography>
              </Box>
            )}
            <FileUploadSection
              title="PO File"
              description="Click or drag file to Fetch from PO"
              onFileChange={(file) => handleFileChange('poFile', file)}
              file={formData.poFile}
            />
          </Box>

          {/* PO Section */}
          <POSection
            formData={formData}
            onInputChange={handleInputChange}
            poVerificationData={poVerificationData}
          />

          {/* Validation Error Message */}
          {isPONumberMissing && (
            <Alert severity="error" sx={{ mb: 2 }}>
              PO number is missing. Please upload a PDF file.
            </Alert>
          )}

          {/* Invoice Details Section */}
          <InvoiceDetailsSection formData={formData} onInputChange={handleInputChange} />
          {isCheckingInvoiceNumber && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Checking Invoice Number uniqueness...
            </Alert>
          )}
          {invoiceNumberExists && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Invoice Number already exists. Please change the Sequence (last part).
            </Alert>
          )}

          {/* Ring Details Section */}
          <RingDetailsSection
            rings={formData.rings}
            onRingChange={handleRingChange}
            onAddRing={handleAddRing}
            onRemoveRing={handleRemoveRing}
            invoiceType={formData.invoiceType}
            invoiceNumber={formData.invoiceNumber}
            amountMismatchError={amountMismatchError}
            ringDetailsData={ringDetailsData}
            poUnitPrice={poUnitPrice}
            poVerificationData={poVerificationData}
            type={formData.type}
          />
          {isRingsIncomplete && !isTypeMissing && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {formData.invoiceType === 'Non-FTTS'
                ? 'Please select section name, category, and payment milestone for each section.'
                : 'Please select ring name, category, and payment milestone for each ring.'}
            </Alert>
          )}

          {/* Invoice Amount Details Section - shows sum of Ring Details amounts (read-only) */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: '18px',
                fontFamily: 'Inter',
                fontWeight: 600,
                mb: 2,
                display: 'flex',
              }}
            >
              Invoice Amount Details
            </Typography>
            <TextField
              fullWidth
              label="Invoice Amount"
              value={ringsAmountSumFormatted}
              size="small"
              disabled
              placeholder="Sum of ring amounts"
              helperText="Sum of amounts from Ring Details"
              FormHelperTextProps={{ sx: { margin: 0, mt: 0.5 } }}
              sx={{
                maxWidth: 400,
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                },
              }}
            />
          </Box>

          {/* Additional Information Section */}
          <AdditionalInfoSection formData={formData} onInputChange={handleInputChange} />

          {/* Vendor Remarks */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: '16px',
                fontFamily: 'Inter',
                fontWeight: 600,
                mb: 1,
                display: 'flex',
              }}
            >
              Vendor Remarks
            </Typography>
            <Box
              component="textarea"
              value={formData.vendorRemarks}
              onChange={(e) => handleInputChange('additional', 'vendorRemarks', e.target.value)}
              placeholder="Enter a description..."
              sx={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontFamily: 'Inter',
                fontSize: '14px',
                resize: 'vertical',
                '&:focus': {
                  outline: 'none',
                  borderColor: '#BD1D23',
                },
              }}
            />
          </Box>

          {/* Other Documents Upload Section */}
          <MultipleFileUploadSection
            description="Click or drag file to this area to upload Other Documents"
            files={formData.otherDocuments}
            onFilesChange={(files) => handleFileChange('otherDocuments', files)}
          />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 4, mb: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{
                borderColor: '#d32f2f',
                color: '#d32f2f',
                textTransform: 'none',
                padding: '8px 24px',
                '&:hover': {
                  borderColor: '#d32f2f',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                },
                '&:disabled': {
                  borderColor: '#ccc',
                  color: '#ccc',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaveDisabled}
              sx={{
                backgroundColor: isSaveDisabled ? '#ccc' : '#d32f2f',
                textTransform: 'none',
                padding: '8px 24px',
                '&:hover': {
                  backgroundColor: isSaveDisabled ? '#ccc' : '#b71c1c',
                },
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#666',
                },
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#666' }} />
                  <span>Saving...</span>
                </Box>
              ) : (
                'Save'
              )}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Error Modal */}
      <Modal
        open={errorModalOpen}
        onClose={() => {
          setErrorModalOpen(false);
          navigate('/vendorInvoiceManagement');
        }}
        aria-labelledby="error-modal-title"
        aria-describedby="error-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography
            id="error-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2, fontWeight: 600 }}
          >
            Error
          </Typography>
          <Typography id="error-modal-description" sx={{ mb: 3 }}>
            {errorModalMessage}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => {
                setErrorModalOpen(false);
                navigate('/vendorInvoiceManagement');
              }}
              sx={{
                backgroundColor: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#b71c1c',
                },
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>
    </DashboardLayout>
  );
};

export default VendorInvoiceForm;
